"use client";

// FOUNDER-WALK-LIVE-RECONCILE-024 · A — THE LINES THE DESIGN HOLDS, SHOWN ON /cart.
//
// THE DEFECT, measured on staging bd11c506 and again on the 023 candidate: a customer picks a Skylynx
// departure inside the founder's design (the transport board, the marketplace drawer), the design's own
// drawer shows the line and its total, and a direct visit to /cart reads "Your cart is empty." Two carts
// exist by architecture (recorded as a residual in FOUNDER-WALK-QA-STAGING-007): the design keeps its
// lines in `localStorage["sg-cart-v1"]` (SG-CART-PERSIST-021/023) and this page reads the host's
// zustand store (`sc-cart`). Neither knew about the other.
//
// WHAT THIS DOES, and what it deliberately does not: it READS the design's persisted lines and renders
// them here with the same edits the design's own cart offers (quantity, remove) and the same way
// forward (the funnel's payment boundary, which mounts the design's own checkout against these same
// lines). Edits are written back to the same key, so the next design page reflects them. It does NOT
// merge the two stores or re-price anything: a line's price is the price the design recorded from
// live's board, and the subtotal is qty × price over those lines — no tax, no tip, no discount invented
// here. Nothing is charged (the funnel's submit refuses unless LEGACY_BOOKING_SUBMIT is set).
//
// The store is read through `useSyncExternalStore`: the server snapshot is "not read yet", so the
// server render and the hydrating client render agree, and the first post-hydration render carries the
// real lines. Writes dispatch a same-document event so this view re-reads without a page reload.

import { useMemo, useSyncExternalStore } from "react";
import { readMountainContext } from "@/components/site/mountain-context";
import { gaItemFromDesignLine, trackRemoveFromCart } from "@/lib/analytics-event";

export const DESIGN_CART_KEY = "sg-cart-v1";
const CHANGE_EVENT = "sg-cart-v1:change";

export interface DesignCartLine {
  id: string;
  name?: string;
  vendor?: string;
  cat?: string;
  qty: number;
  price: number;
  /** transport legs: the board's own time-over-stop pair */
  dep?: string;
  arr?: string;
  depStop?: string;
  /** FWLR-025-R7 · L — the journey this leg belongs to (see the export's commitRide) */
  tripId?: string;
  journey?: string;
  legKind?: string;
  klass?: string;
  who?: string;
  oneWayGuest?: number | null;
  oneWayMember?: number | null;
  arrStop?: string;
  date?: string;
  legName?: string;
  /** the mountain a hosted design stamped on a marketplace line when it was added (SG_HOSTED_MTN_STAMP) */
  mountainCode?: string;
  /** the native listing documentId the live feed published for this exact `store-<id>` row, copied at add time (an identity, never an amount) */
  listingId?: string;
  time?: string;
  /** rentals: the hire window */
  start?: string;
  end?: string;
  /**
   * SNOWCLUB-LAUNCH-REMAINING-PARITY-001 — the schedule's own identifier and the SearchCriteriaDTO
   * fields this leg was searched under (§18b), stamped by the design's own `commitRide` alongside the
   * fields above. `BookingSteps.mergeDesignLegs` maps `sgSid` onto `CartItem.itemIdentifier` and the
   * rest onto `CartItem.searchCriteria`, exactly as `BookingResults.tsx`'s host-store add path already
   * does for a leg picked outside the design. Optional: an older persisted line, or a design-time
   * (non-hosted) constant, simply has none, and the booking submission refuses rather than inventing one.
   */
  sgSid?: string | number;
  departureCode?: string;
  arrivalCode?: string;
  travelDate?: string;
  returnDate?: string;
  adult?: number;
  child?: number;
  senior?: number;
  student?: boolean;
  noStore?: boolean;
  returnTrip?: boolean;
}

interface Stored {
  v: 1;
  cart: DesignCartLine[];
  cartTip: number | null;
  /** Whether $0 is a deliberate rider choice rather than a pre-marker legacy default. */
  cartTipSet?: boolean;
}

type ReadableStorage = Pick<Storage, "getItem"> | null | undefined;
type WritableStorage = Pick<Storage, "getItem" | "setItem"> | null | undefined;

const isLine = (l: unknown): l is DesignCartLine => {
  if (!l || typeof l !== "object") return false;
  const c = l as Partial<DesignCartLine>;
  return typeof c.id === "string" && Number.isFinite(+(c.qty ?? NaN)) && +(c.qty ?? 0) > 0 && Number.isFinite(+(c.price ?? NaN)) && +(c.price ?? -1) >= 0;
};

/** The persisted design cart, defensively read: a missing or corrupt store is an empty cart, never a throw. */
export function readDesignCart(storage: ReadableStorage): DesignCartLine[] {
  try {
    const raw = storage?.getItem(DESIGN_CART_KEY);
    if (!raw) return [];
    const saved = JSON.parse(raw) as Partial<Stored> | null;
    if (saved?.v !== 1 || !Array.isArray(saved.cart)) return [];
    return saved.cart.filter(isLine);
  } catch {
    return [];
  }
}

/** Write the lines back in the design's own shape, keeping the tip it recorded. */
export function writeDesignCart(storage: WritableStorage, lines: DesignCartLine[]): void {
  try {
    let tip: number | null = null;
    let tipSet = false;
    try {
      const prev = JSON.parse(storage?.getItem(DESIGN_CART_KEY) ?? "null") as Partial<Stored> | null;
      const t = prev?.cartTip;
      tip = typeof t === "number" && Number.isFinite(t) ? t : null;
      tipSet = prev?.cartTipSet === true || (tip != null && tip > 0);
    } catch {
      tip = null;
    }
    storage?.setItem(DESIGN_CART_KEY, JSON.stringify({ v: 1, cart: lines, cartTip: tip, cartTipSet: tipSet }));
  } catch {
    /* storage unavailable: the page still renders what it read */
  }
}

/**
 * The identity of the cart a quote answers. SG_CART_KEY in design-sources.ts builds the SAME string from the design's
 * live state, so the design can tell a quote for this cart from one for the cart before the last edit. No money here.
 */
export const designCartKey = (lines: DesignCartLine[]): string =>
  lines
    .map((l) => [l.id, l.qty, l.price, l.date ?? "", l.start ?? "", l.end ?? "", l.time ?? "", l.dep ?? "", l.sgSid ?? ""].join(":"))
    .sort()
    .join("|");

/** The driver tip the rider chose (transport carts only); the existing order planner refuses a tip above $0. */
export function readDesignCartTip(storage: ReadableStorage, lines: DesignCartLine[]): number {
  if (!lines.some((l) => l.cat === "Transport")) return 0;
  try {
    const t = (JSON.parse(storage?.getItem(DESIGN_CART_KEY) ?? "null") as Partial<Stored> | null)?.cartTip;
    return typeof t === "number" && Number.isFinite(t) && t > 0 ? t : 0;
  } catch {
    return 0;
  }
}

/** qty × price over the design's lines — no tax, tip or discount is derived here. */
export const designCartSubtotal = (lines: DesignCartLine[]): number =>
  lines.reduce((sum, l) => sum + +l.qty * +l.price, 0);

/**
 * Where these lines check out: the funnel's payment boundary for the mountain the visitor is on.
 *
 * ── SNOWCLUB-LAUNCH-REMAINING-PARITY-001 — ONE PAGE, ONE ACTION, TWO DESTINATIONS ────────────────
 * This returned `/booking/details`, and `/cart` renders BOTH line blocks: the host store's, whose
 * PROCEED resolves through `funnelCheckoutHref` to `/mountain-<m>/booking/review`, and this one.
 * So the same page offered "PROCEED TO CHECKOUT" twice, to two different steps, depending only on
 * which cart happened to hold the leg — and a design-held leg (the only kind the founder's own
 * board produces since the A4 swap) took the one that is no longer the entry.
 *
 * `/booking/review` is the entry: FOUNDER-QA-FOLLOWUP-008 · item 4 moved the pay rail to open at MY
 * CART on the route-owned `/booking/review` URL, and QA-BATCH-2 · item 14 removed the traveller step
 * that `/booking/details` was the front door of (purchaser and traveller are now collected with the
 * card details). Measured on the served candidate before this change: `/cart` → PROCEED →
 * `/mountain-whistler/booking/details`, which is not the payment step the control promises.
 *
 * It is written as a delegation to `funnelCheckoutHref`'s rule rather than as a second copy of the
 * string, because two copies of this destination is exactly how the two surfaces drifted apart —
 * the same lesson `cart-billing` records for `checkoutHref` ("each surface owning its own copy is
 * how BOTH ended up pointing at /checkout/<id>"). `cart-billing` keys on host `CartItem`s, which a
 * design line is not, so the shared thing is the shape and it is asserted by a test rather than
 * imported through a type this module does not have.
 */
export const designCartCheckoutHref = (mountainCode: string): string =>
  `/mountain-${mountainCode}/booking/review`;

const fmt = (n: number): string => `$${n.toFixed(n % 1 === 0 ? 0 : 2)}`;

const subscribe = (onChange: () => void): (() => void) => {
  window.addEventListener("storage", onChange);
  window.addEventListener(CHANGE_EVENT, onChange);
  return () => {
    window.removeEventListener("storage", onChange);
    window.removeEventListener(CHANGE_EVENT, onChange);
  };
};
const getSnapshot = (): string => {
  try {
    return window.localStorage.getItem(DESIGN_CART_KEY) ?? "";
  } catch {
    return "";
  }
};
/** The server has no store to read: null means "not read yet", distinct from an empty cart. */
const getServerSnapshot = (): string | null => null;

/** FWLR-025-R7 · J — the design's lines for any host screen (the 6-step DETAILS / REVIEW read them beside the host cart). */
export function useDesignCartLines(): DesignCartLine[] | null {
  const raw = useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);
  return useMemo(() => (raw === null ? null : readDesignCart({ getItem: () => raw })), [raw]);
}

/** FWLR-025-R7 · L — the journey caption printed above the first leg of each journey. */
export function journeyLabel(l: DesignCartLine): string {
  return `${l.journey === "round-trip" ? "Round trip" : "One-way"} · ${l.qty} ${l.qty === 1 ? "rider" : "riders"}${l.vendor ? ` · ${l.vendor}` : ""}${l.klass ? ` · ${l.klass}` : ""}`;
}

/**
 * FWLR-025-R7 · L — A LINKED ROUND-TRIP LEG IS NEVER REMOVED ALONE. Removing either leg of a round-trip journey asks to
 * remove the whole journey; declining offers to keep the other leg as a ONE-WAY at the one-way fare that leg recorded
 * when it was picked, with a second explicit confirmation. Returns the next lines, or null when nothing changes.
 * `ask` is injected so the rule can be unit-tested without a window.
 */
export function removeDesignLine(lines: DesignCartLine[], id: string, ask: (msg: string) => boolean, signedIn: boolean): DesignCartLine[] | null {
  const l = lines.find((x) => x.id === id);
  if (!l) return null;
  const linked = l.cat === "Transport" && l.tripId && l.journey === "round-trip" ? lines.filter((x) => x.cat === "Transport" && x.tripId === l.tripId && x.id !== l.id) : [];
  if (!linked.length) return lines.filter((x) => x.id !== id);
  if (ask(`This ${l.legName ?? "leg"} is part of a round trip. Remove BOTH legs of the journey?\n\nOK removes the whole round trip. Cancel keeps it - you can then choose to keep the other leg as a one-way.`)) {
    return lines.filter((x) => !(x.cat === "Transport" && x.tripId === l.tripId));
  }
  const other = linked[0]!;
  const who = other.who ?? (signedIn ? "member" : "guest");
  const ow = who === "member" && other.oneWayMember != null ? other.oneWayMember : other.oneWayGuest;
  if (ow == null) return null;
  if (!ask(`Keep the ${other.legName ?? "other leg"} as a ONE-WAY ticket at its one-way fare ($${Math.round(ow)} per rider) instead?\n\nOK reprices it as one-way and removes this leg. Cancel changes nothing.`)) return null;
  return lines.filter((x) => x.id !== id).map((x) => (x.id === other.id ? { ...x, price: ow, journey: "one-way" } : x));
}

export default function DesignCartLines({
  onEmpty,
}: {
  /** rendered when the design holds nothing, so the page can keep its own empty copy */
  onEmpty?: React.ReactNode;
}) {
  const raw = useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);
  const lines = useMemo(() => (raw === null ? null : readDesignCart({ getItem: () => raw })), [raw]);

  const update = (next: DesignCartLine[]) => {
    writeDesignCart(window.localStorage, next);
    window.dispatchEvent(new Event(CHANGE_EVENT));
  };
  // FWLR-025-R7 · L — every removal (the Remove control and a quantity stepped to zero) goes through the journey rule
  const removeLine = (id: string) => {
    const next = removeDesignLine(lines ?? [], id, (msg) => window.confirm(msg), false);
    if (next) {
      // Reported from what actually left the cart: removing one leg of a round trip can take both, or keep
      // the other repriced as a one-way, and the confirm() the visitor answered decides which.
      trackRemoveFromCart((lines ?? []).filter((x) => !next.some((n) => n.id === x.id)).map(gaItemFromDesignLine));
      update(next);
    }
  };

  // SSR and the hydrating render agree on "not read yet"; the store is read once the client owns the tree.
  if (lines === null) return null;
  if (lines.length === 0) return <>{onEmpty ?? null}</>;

  const mountain = readMountainContext();
  const subtotal = designCartSubtotal(lines);
  return (
    <section data-cart-page-design-lines data-cart-page-design-count={lines.length} style={box}>
      <div style={head}>
        <span style={{ flex: 1, fontWeight: 700, letterSpacing: "0.5px", color: "#444" }}>IN YOUR CART</span>
        <span data-cart-page-design-subtotal style={{ fontWeight: 700 }}>{fmt(subtotal)}</span>
      </div>
      <ul style={list}>
        {lines.map((l, li) => {
          // FWLR-025-R7 · L — the first leg of a journey carries the journey's caption
          const firstOfTrip = l.cat === "Transport" && !!l.tripId && lines.findIndex((x) => x.tripId === l.tripId) === li;
          const when = l.dep && l.depStop
            ? `${l.dep} ${tidy(l.depStop)}${l.arr ? ` → ${l.arr}` : ""}${l.arrStop ? ` ${tidy(l.arrStop)}` : ""}`
            : l.start
              ? `${l.start}${l.end ? ` → ${l.end}` : ""}`
              : "";
          return (
            <li key={l.id} data-cart-page-design-line={l.id} data-cart-page-design-trip={l.tripId ?? ""} style={row}>
              {firstOfTrip ? (
                <div data-cart-page-design-journey={l.tripId} style={{ width: "100%", fontSize: 11, fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", color: "#fc500e" }}>
                  {journeyLabel(l)}
                </div>
              ) : null}
              <div data-cart-page-design-details style={details}>
                <div style={{ fontWeight: 600, fontSize: 14 }}>{l.name ?? l.cat ?? "Item"}</div>
                <div style={{ fontSize: 12, color: GREY }}>
                  {[l.vendor, l.date, when].filter(Boolean).join(" · ")}
                </div>
              </div>
              <div data-cart-page-design-controls style={controls}>
                <div style={qtyBox}>
                  <button
                    type="button"
                    aria-label="Fewer"
                    data-cart-page-design-dec
                    onClick={() => (l.qty > 1 ? update(lines.map((x) => (x.id === l.id ? { ...x, qty: x.qty - 1 } : x))) : removeLine(l.id))}
                    style={qtyBtn}
                  >
                    −
                  </button>
                  <span data-cart-page-design-qty style={{ minWidth: 18, textAlign: "center" }}>{l.qty}</span>
                  <button
                    type="button"
                    aria-label="More"
                    data-cart-page-design-inc
                    onClick={() => update(lines.map((x) => (x.id === l.id ? { ...x, qty: x.qty + 1 } : x)))}
                    style={qtyBtn}
                  >
                    +
                  </button>
                </div>
                <div data-cart-page-design-line-total style={{ width: 72, textAlign: "right", fontWeight: 700 }}>{fmt(+l.qty * +l.price)}</div>
                <button
                  type="button"
                  data-cart-page-design-remove
                  aria-label={`Remove ${l.name ?? "item"}`}
                  onClick={() => removeLine(l.id)}
                  style={removeBtn}
                >
                  Remove
                </button>
              </div>
            </li>
          );
        })}
      </ul>
      <div style={foot}>
        <span style={{ fontSize: 12, color: GREY }}>Cart items are not reserved. Nothing is charged now; submitting authorises your card, which is charged only if the booking is confirmed.</span>
        <a data-cart-page-design-checkout href={designCartCheckoutHref(mountain)} style={checkoutBtn}>
          PROCEED TO CHECKOUT
        </a>
      </div>
    </section>
  );
}

const tidy = (v: string): string =>
  String(v)
    .replace(/\s*\([^)]*\)/g, "")
    .toLowerCase()
    .replace(/(^|[\s-])([a-z])/g, (_m, a: string, b: string) => a + b.toUpperCase())
    .replace(/\b(Yvr|Yyc|Ylw|Yyz|Yul)\b/g, (w) => w.toUpperCase())
    .trim();

const ORANGE = "#f15a22";
const GREY = "#828282";
const RULE = "#e3e8ea";
const box: React.CSSProperties = { border: `1px solid ${RULE}`, borderRadius: 8, marginBottom: 16, background: "#fff" };
const head: React.CSSProperties = { display: "flex", alignItems: "center", gap: 10, padding: "12px 16px", borderBottom: `1px solid ${RULE}`, fontSize: 13 };
const list: React.CSSProperties = { listStyle: "none", margin: 0, padding: 0 };
const row: React.CSSProperties = { display: "flex", alignItems: "center", gap: 12, padding: "12px 16px", borderBottom: `1px solid ${RULE}`, flexWrap: "wrap" };
// FOUNDER-WALK-LIVE-RECONCILE-024 · A2 — THE ITINERARY KEEPS A READABLE COLUMN AT 390.
// Measured by the non-authoring verifier on dab0bee4: `flex: 1, minWidth: 0` let this block shrink to
// 49.3125px inside the 332px mobile row, so "2026-09-13" broke across lines and the stop names stacked
// one word per line (253px tall for one leg). `minWidth: 0` is still required — without it a long stop
// name would push the row wider than the viewport — so the fix is the BASIS, not the shrink: 260px is
// wider than the mobile row can spare beside the controls, so the controls wrap to their own line and
// the details grow to the full row; at 1440 the same line still fits (basis + controls ≈ 500px of
// 1098px), so the desktop layout the verifier measured as correct is unchanged.
const details: React.CSSProperties = { flex: "1 1 260px", minWidth: 0 };
// The three controls travel together, so they wrap as one block instead of splitting across two lines.
const controls: React.CSSProperties = { display: "flex", alignItems: "center", gap: 12, marginLeft: "auto", flex: "0 0 auto" };
const qtyBox: React.CSSProperties = { display: "flex", alignItems: "center", gap: 6 };
const qtyBtn: React.CSSProperties = { width: 28, height: 28, border: `1px solid ${RULE}`, borderRadius: 6, background: "#fff", cursor: "pointer", fontSize: 16, lineHeight: 1 };
// row wraps so a journey caption can sit above its first leg
const removeBtn: React.CSSProperties = { border: "none", background: "none", color: GREY, textDecoration: "underline", cursor: "pointer", fontSize: 12 };
const foot: React.CSSProperties = { display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, padding: "12px 16px", flexWrap: "wrap" };
const checkoutBtn: React.CSSProperties = { display: "inline-block", padding: "12px 20px", background: ORANGE, color: "#fff", borderRadius: 8, fontWeight: 700, fontSize: 13, letterSpacing: "0.06em", textDecoration: "none" };
